import type { EventInput } from "@fullcalendar/core";
import type { Reservation } from "@/types/reservation";
import type { Pc } from "@/types/pc";
import { STATUS_COLORS, STATUS_LABELS } from "@/lib/status";
import { dateKey } from "@/lib/date";

export function reservationsToEvents(
  reservations: Reservation[],
  pcById: Map<number, Pc>,
  userNameById: Map<number, string>
): EventInput[] {
  return reservations.map((r) => {
    const color = STATUS_COLORS[r.status];
    const pcName = pcById.get(r.pc_id)?.name ?? `PC#${r.pc_id}`;
    const userName = userNameById.get(r.user_id) ?? "";
    return {
      id: String(r.id),
      title: `${userName} / ${pcName}`,
      start: r.start_at,
      end: r.end_at,
      backgroundColor: color.bg,
      borderColor: color.border,
      textColor: color.fg,
      extendedProps: { reservation: r, statusLabel: STATUS_LABELS[r.status] },
    };
  });
}

function eachDateKeyInRange(startKey: string, endKey: string): string[] {
  const [sy, sm, sd] = startKey.split("-").map(Number);
  const [ey, em, ed] = endKey.split("-").map(Number);
  const start = Date.UTC(sy, sm - 1, sd);
  const end = Date.UTC(ey, em - 1, ed);
  const keys: string[] = [];
  for (let t = start; t <= end; t += 86400000) {
    const d = new Date(t);
    keys.push(
      `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`
    );
  }
  return keys;
}

/**
 * Groups reservations by every Asia/Tokyo calendar date they occupy (not just
 * the start date), so a multi-day reservation shows up — and is counted — on
 * each day of the mobile agenda it actually spans, not only the first one.
 */
export function groupByTokyoDate(reservations: Reservation[]): Map<string, Reservation[]> {
  const map = new Map<string, Reservation[]>();
  for (const r of reservations) {
    const startKey = dateKey(r.start_at);
    // An end time that lands exactly on a day boundary (e.g. 00:00) shouldn't
    // count that next day as occupied, so step back 1ms before reading it.
    const endKey = dateKey(new Date(new Date(r.end_at).getTime() - 1).toISOString());
    for (const key of eachDateKeyInRange(startKey, endKey)) {
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
  }
  return map;
}
